import { useGetSuperAdminCardpayDaily } from '@agos/api-client/platform'
import type { CardPayDay, CardPayFranchise, CardPayRefund } from './cardpay'
import {
  CARDPAY_PRESETS,
  T,
  WHOLE_DEFAULT_WINDOW,
  formatCents,
  formatCount,
  formatDay,
  reportWindowParams,
  tt,
  type ReportWindow,
} from '@agos/shared'
import { useMemo, useState } from 'react'
import { Banknote, CreditCard, Landmark, Percent, PiggyBank, Undo2 } from 'lucide-react'
import { PageHeader, type Column, DataTable, ReadFailed, ReportWindowBar, Spinner, StatCard } from '@agos/ui'

import {
  cardPayReportOf,
  inconsistentNote,
  isLosing,
  marginPct,
  marginWarning,
  monthTotals,
  refundListNote,
  refundTotals,
  showsFigures,
  unreachableNote,
  volumeNote,
} from './cardpay'

/**
 * What the platform takes on card payments, and what it pays to take it.
 *
 * A negative margin means the platform is paying to process somebody else's payments. Legacy colours it
 * red, which is right and is kept — with a sentence beside it, because colour alone is the signal that
 * gets missed on a dashboard of numbers.
 */
export function CardPayPage() {
  // §3.84 - `/cardpay/daily` declares `from` and `to`; this screen sent neither, so it showed the
  // server's default period and nothing else. Legacy's presets start at *Včera* and *Dnes*, which is
  // the question somebody opening a card-payment report at 09:00 actually has.
  const [window, setWindow] = useState<ReportWindow>(WHOLE_DEFAULT_WINDOW)
  const { data, isLoading, isError } = useGetSuperAdminCardpayDaily(reportWindowParams(window))
  const report = useMemo(() => cardPayReportOf(data), [data])

  /**
   * The eight columns legacy draws per franchise, per day - §3.93.
   *
   * **Built per franchise** because two of the captions carry that franchise's own rates: legacy
   * writes `Provízia (1,2 %)` and `TB (0,9 %)` into the headers, and a shared column list could not.
   *
   * Every figure is sortable, which legacy's static table is not - *which day did we lose money* is
   * the question somebody opens this with, and it cannot be answered by reading thirty rows.
   *
   * `Netto poslať` is `netPayoutCents`, `Na vyplatenie` is `payoutCents`. The second is the one
   * §3.93's own allow-list wrongly claimed was already drawn; its only readers were the parse and
   * `inconsistentNote`'s cross-check.
   */
  const dailyColumns = (franchise: CardPayFranchise): Column<CardPayDay>[] => {
    const money = (
      key: string,
      header: string,
      value: (day: CardPayDay) => number,
      hook: string,
    ): Column<CardPayDay> => ({
      key,
      header,
      className: 'text-right whitespace-nowrap',
      headerClassName: 'text-right',
      sortable: true,
      sortValue: value,
      render: (day) => <span data-cardpay-day-figure={hook}>{formatCents(value(day))}</span>,
    })

    return [
      {
        key: 'day',
        header: tt('Deň'),
        className: 'whitespace-nowrap',
        sortable: true,
        sortValue: (day) => day.day,
        render: (day) => day.day,
      },
      {
        key: 'payments',
        header: tt('Platby'),
        className: 'text-right',
        headerClassName: 'text-right',
        sortable: true,
        sortValue: (day) => day.payments,
        render: (day) => <span data-cardpay-day-payments>{formatCount(day.payments)}</span>,
      },
      money('gross', 'CardPay 100 %', (day) => day.grossCents, 'gross'),
      money(
        'markup',
        `${tt('Provízia')} (${formatCount(franchise.markupPct)} %)`,
        (day) => day.markupCents,
        'markup',
      ),
      money('payout', tt('Na vyplatenie'), (day) => day.payoutCents, 'payout'),
      money('refunded', tt('Refundy'), (day) => day.refundedCents, 'refunded'),
      money('net', tt('Netto poslať'), (day) => day.netPayoutCents, 'net'),
      money(
        'tb',
        `${tt('Poplatok banky')} (${formatCount(franchise.tbCostPct)} %)`,
        (day) => day.tbCostCents,
        'tb',
      ),
      // §3.113 - legacy's ninth column, and the only one that says whether a **day** was profitable.
      // Provízia less the bank's fee is not a constant: a refunded payment takes its markup back and
      // leaves the fee paid, so a day can run negative while the month does not.
      money('margin', tt('Podiel'), (day) => day.marginCents, 'margin'),
    ]
  }

  return (
    <main className="space-y-3" data-cardpay>
      {/* §3.78 - the title block every other screen in the product opens with. This page had
        * none at all: you landed on it and nothing on the screen said what it was. */}
      {/* §3.190 - `CardpayReportService` reads `online_payments WHERE gateway = 'tatrapay'`, so a franchise taking
          cards through GP webpay is not in these figures. Said, because a franchise missing from a card report reads
          as a franchise that took no cards. */}
      <PageHeader
        title={'CardPay'}
        description={tt(
          'Denné hlásenia platieb kartou cez TatraPay+. Platby cez GP webpay (Slovenská sporiteľňa) tu nie sú.',
        )}
      />

      <ReportWindowBar
        window={window}
        onChange={setWindow}
        presets={CARDPAY_PRESETS}
        defaultNote={tt('Predvolené obdobie serveru')}
      />
      {/* §3.82 - a screen that cannot load its data must not render its empty state.
        * Discarding `isLoading` and `isError` meant *"Za toto obdobie neboli zaznamenané žiadne platby kartou"* appeared while
        * the request was in flight and again when it failed - so a broken load read as a quiet
        * period, which is §3.52's argument applied to the six screens it did not reach. */}
      {isError && (
        <ReadFailed marker="cardpay-error"><T>Hlásenia CardPay sa nepodarilo načítať.</T></ReadFailed>
      )}
      {isLoading && (
        <div className="py-10 flex justify-center" data-cardpay-loading>
          <Spinner />
        </div>
      )}


      <div className="space-y-2" data-cardpay-platform>
        <p className="text-xs text-muted-foreground" data-cardpay-rates>
          <T>Provízia</T> {formatCount(report.defaultMarkupPct)} % · <T>Poplatok banky</T>{' '}
          {formatCount(report.tbCostPct)} %
        </p>
        {/* §3.93 - **six figures, where this screen showed three.** `CardPayTotals` answers ten
          * fields; the page reached four of them through helpers and two of those only inside a
          * warning. So it printed the *answer* - `Náš podiel` - and none of the arithmetic: what the
          * platform charged, what the bank took, what is being paid out, what came back.
          *
          * A margin nobody can check is worse on a money screen than anywhere else, and legacy draws
          * all six. Ported label for label, as `StatCard` - which this page used **no** shared
          * component at all before. */}
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
          <StatCard
            label={`CardPay 100 % (${formatCount(report.platform.payments)} ${tt('platieb')})`}
            value={formatCents(report.platform.grossCents)}
            icon={CreditCard}
            color="violet"
            testId="cardpay-gross"
          />
          <StatCard
            label={tt('Netto na vyplatenie')}
            value={formatCents(report.platform.netPayoutCents)}
            icon={Banknote}
            color="emerald"
            testId="cardpay-net-payout"
          />
          <StatCard
            /* Nought as nought, not legacy's em dash: this repository does not use one, and *no
             * refunds* is a figure rather than an absence. */
            label={`${tt('Refundy')} (${formatCount(report.platform.refundedCount)})`}
            value={formatCents(report.platform.refundedCents)}
            icon={Undo2}
            color={report.platform.refundedCents > 0 ? 'amber' : 'primary'}
            testId="cardpay-refunded"
          />
          <StatCard
            label={tt('Naša provízia')}
            value={formatCents(report.platform.markupCents)}
            icon={Percent}
            testId="cardpay-markup"
          />
          <StatCard
            label={`${tt('Poplatok banky')} (${formatCount(report.tbCostPct)} %)`}
            value={formatCents(report.platform.tbCostCents)}
            icon={Landmark}
            color="red"
            testId="cardpay-tb-cost"
          />
          <div data-cardpay-margin={isLosing(report.platform) ? 'negative' : 'positive'}>
            <StatCard
              label={tt('Náš podiel (čistý)')}
              value={formatCents(report.platform.marginCents)}
              icon={PiggyBank}
              color={isLosing(report.platform) ? 'red' : 'emerald'}
              testId="cardpay-margin"
            />
          </div>
        </div>
        {/* Kept as its own line rather than folded into a card: it is the platform's volume in one
          * sentence and two specs read it. */}
        <p className="text-xs text-muted-foreground" data-cardpay-volume>
          {volumeNote(report.platform)}
        </p>
        {inconsistentNote(report.platform) !== '' && (
          <p className="text-[11px] text-amber-500" data-cardpay-inconsistent>
            {inconsistentNote(report.platform)}
          </p>
        )}
      </div>

      {/* §3.52 - an explicit nothing, for the same reason the per-franchise failure note above
        * exists: a screen that simply stops after the platform tile cannot say whether no franchise
        * took a card payment or whether none was asked. */}
      {!isLoading && !isError && report.franchises.length === 0 && (
        <p
          className="rounded-2xl border border-dashed border-border p-4 text-xs text-muted-foreground"
          data-cardpay-empty
        >
          <T>Za toto obdobie neboli zaznamenané žiadne platby kartou.</T>
        </p>
      )}

      {report.franchises.map((franchise) => (
        <div key={franchise.slug} className="rounded-2xl border border-border p-3 space-y-1" data-cardpay-franchise={franchise.slug}>
          <p className="text-sm font-semibold">{franchise.name}</p>
          {/* §3.44 - **a zero the screen cannot vouch for is worse than no figure.** The server
            * records a per-franchise failure on purpose and this page ignored it, so a franchise
            * whose database was down rendered 0,00 € at a plausible margin - identical to one with
            * no card payments. `markupPct` and `tbCostPct` survive the failure because they come
            * from the settings, which is what made the row look measured. */}
          {showsFigures(franchise) ? (
            <>
              <p className="text-[11px] text-muted-foreground">
                {formatCount(franchise.markupPct)} % − {formatCount(franchise.tbCostPct)} % ={' '}
                <span data-cardpay-margin-pct>{formatCount(marginPct(franchise))} %</span>
              </p>
              <p className="text-xs" data-cardpay-franchise-volume>
                {volumeNote(franchise.totals)}
              </p>
              {marginWarning(franchise) !== '' && (
                <p className="text-[11px] text-red-500" data-cardpay-losing>
                  {marginWarning(franchise)}
                </p>
              )}
            </>
          ) : (
            <p className="text-xs font-semibold text-red-500" data-cardpay-unreachable>
              {unreachableNote(franchise)}
            </p>
          )}

          {/* §3.93 - **the day-by-day reconciliation, which was parsed and never drawn.**
            * `CardPayFranchise.daily` is in the response, `cardpay.ts` parses it, and this page
            * referenced `.daily` zero times - on a screen fed by an endpoint called `/cardpay/daily`,
            * whose whole reason for existing is the per-day breakdown. Legacy draws these eight
            * columns; AGOS drew a name, a volume sentence and a margin percentage.
            *
            * Sortable by day and by every figure, which legacy's static table is not: *which day did
            * we lose money* is the question this table is opened with.
            *
            * Only where the figures are real - the same `showsFigures` gate the block above uses, for
            * the same reason: on an unreachable franchise every one of these is the initialised zero
            * rather than a measurement. */}
          {showsFigures(franchise) && franchise.daily.length > 0 && (
            <div className="pt-2" data-cardpay-daily={franchise.slug}>
              <DataTable<CardPayDay>
                columns={dailyColumns(franchise)}
                data={franchise.daily}
                rowKey={(day) => day.day}
                rowAttributes={(day) => ({ 'data-cardpay-day': day.day })}
                emptyMessage={tt('Za toto obdobie žiadne platby')}
                compact
                pageSize={31}
              />
              {/* §3.113 - legacy's `<tfoot>`, which this page had no counterpart for. *What do we send
                * this franchise for the month* was a sum done by eye down a column of up to 31 rows, on
                * the one screen whose whole subject is a payout. Read off `franchise.totals` rather than
                * summed from the rows above: the server rounds per payment, so a sum here would disagree
                * with the invoice by a cent or two every month. */}
              <div
                className="mt-2 flex flex-wrap gap-x-4 gap-y-1 rounded-xl border border-border px-3 py-2 text-[11px]"
                data-cardpay-month-totals={franchise.slug}
              >
                <span className="font-bold uppercase tracking-wider text-muted-foreground">
                  <T>Spolu</T>
                </span>
                {monthTotals(franchise).map((figure) => (
                  <span key={figure.hook} data-cardpay-total={figure.hook}>
                    <span className="text-muted-foreground">{figure.label}</span>{' '}
                    <span
                      className={
                        figure.hook === 'margin' && figure.cents < 0 ? 'font-bold text-red-500' : 'font-bold'
                      }
                    >
                      {formatCents(figure.cents)}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* §3.42 - the refunds themselves. §3.28 gave this screen `reversedMarkupCents`, the markup
            * handed back on a refund, computed from the daily totals - so the page has carried
            * arithmetic about refunds since then and never shown one. Measured: 30 refunded TatraPay
            * payments totalling 551,65 EUR against 1 249 paid.
            *
            * Hidden where there are none: two of the three franchises have no card volume at all,
            * and a heading over an empty table says less than no table does. */}
          {franchise.refunds.length > 0 && (
            <div className="pt-1 space-y-1" data-cardpay-refunds={franchise.slug}>
              {/* **Read off `franchise.totals`, not off the rows below - FB-935, §3.172.** The
                * server caps the detail list at a hundred with no total of its own, so summing it
                * understated all three of these above that, while the platform card at the top of
                * the screen and the daily `Refundy` column stayed right. The aggregate is the whole
                * window at any size. */}
              <p className="text-[11px] text-muted-foreground" data-cardpay-refund-summary>
                <T>Vrátené</T>{' '}
                <span data-cardpay-refund-count>
                  {formatCount(refundTotals(franchise.totals).count)}
                </span>{' '}
                &middot; {formatCents(refundTotals(franchise.totals).amountCents)}
                {' · '}
                {/* **Two sums, because they are two questions.** What went back to customers, and
                  * what it cost the platform - which is its markup on it rather than the whole
                  * amount, and the figure §3.28's identity turns on. */}
                <T>z toho naša marža</T>{' '}
                {formatCents(refundTotals(franchise.totals).clawbackCents)}
              </p>
              {/* §3.100 - the last hand-rolled list on this screen. The daily breakdown beside it
                * has been a `DataTable` since §3.93, and one card carrying both a real table and a
                * hand-rolled one is the inconsistency somebody notices without being able to name
                * it. Sortable by amount, which is the question: **which refund was the big one**. */}
              <DataTable<CardPayRefund>
                columns={REFUND_COLUMNS}
                data={franchise.refunds}
                rowKey={(refund) => refund.id}
                // Kept: `tower.spec.ts` locates a refund row by this.
                rowAttributes={(refund) => ({ 'data-cardpay-refund': String(refund.id) })}
                compact
                // **Whatever the server sent, on one page.** This comment used to read *every refund
                // in the window*, which `REFUND_DETAIL_LIMIT = 100` makes false above a hundred -
                // FB-935. A pager under a four-row table is still a control with no question behind
                // it, so the shortfall is said in words rather than paged through: the missing rows
                // cannot be fetched at all, exactly as on the audit log.
                pageSize={Math.max(franchise.refunds.length, 1)}
                className="text-[11px]"
              />
              {refundListNote(franchise.refunds.length, franchise.totals) !== '' && (
                <p className="text-[11px] font-semibold text-amber-500" data-cardpay-refunds-capped>
                  {refundListNote(franchise.refunds.length, franchise.totals)}
                </p>
              )}
            </div>
          )}
        </div>
      ))}
    </main>
  )
}

/**
 * The refund columns - §3.100.
 *
 * Module-level rather than built per franchise, unlike `dailyColumns`: a refund row carries
 * everything these read, so there is nothing to close over and a fresh array per franchise per
 * render would be the only effect.
 */
const REFUND_COLUMNS: Column<CardPayRefund>[] = [
  {
    key: 'order',
    header: tt('Objednávka'),
    className: 'font-mono',
    sortable: true,
    sortValue: (refund) => refund.orderNumber,
    render: (refund) => (refund.orderNumber === '' ? '-' : refund.orderNumber),
  },
  {
    /*
     * §3.185, FB-959 - **legacy's *Zaplatené*, which the server has always answered and nothing drew.**
     * `CardpayReportService` selects `paid_at` and sends `paidAt`, `refundsOf` parses it and
     * `CardPayRefund` declares it; `REFUND_COLUMNS` had four columns against legacy's five and this was
     * the one missing. §3.122's shape, found a fifth time.
     *
     * **Before `Vrátené`, so the two dates read in order** - paid, then refunded - which is legacy's own
     * ordering and the only one under which the gap between them is readable at a glance.
     *
     * **The dash is the state that matters on this data.** Šurany holds 35 refunded card payments and
     * **6 carry a `paid_at` at all**: `instant(null)` answers null and `refundText` makes it `''`, so
     * without this column a refund whose payment date is unknown looked exactly like one paid yesterday.
     * The clawback is deducted from a payout, so *which payment did this come from* is the question an
     * operator reconciling it has to be able to ask - and on 29 of 35 rows the honest answer is that
     * nobody knows. **Not amber and not a sentence**: a legacy row with no payment date is not a fault
     * this screen can fix, and FB-818 is the entry where a screen invented a problem out of an absence.
     */
    key: 'paidAt',
    header: tt('Zaplatené'),
    className: 'text-muted-foreground whitespace-nowrap',
    sortable: true,
    sortValue: (refund) => refund.paidAt,
    render: (refund) => (refund.paidAt === '' ? '-' : formatDay(refund.paidAt)),
  },
  {
    key: 'refundedAt',
    header: tt('Vrátené'),
    className: 'text-muted-foreground whitespace-nowrap',
    sortable: true,
    sortValue: (refund) => refund.refundedAt,
    render: (refund) => (refund.refundedAt === '' ? '-' : formatDay(refund.refundedAt)),
  },
  {
    key: 'amount',
    header: tt('Suma'),
    className: 'text-right tabular-nums',
    headerClassName: 'text-right',
    sortable: true,
    sortValue: (refund) => refund.amountCents,
    render: (refund) => formatCents(refund.amountCents),
  },
  {
    key: 'clawback',
    header: tt('Naša marža'),
    // Red because it is money going out of the platform, which is what §3.28's identity turns on -
    // not a state, so it stays a coloured figure rather than becoming a badge.
    className: 'text-right tabular-nums text-red-500',
    headerClassName: 'text-right',
    sortable: true,
    sortValue: (refund) => refund.clawbackCents,
    render: (refund) => formatCents(refund.clawbackCents),
  },
]
